import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { Calendar } from "primereact/calendar";
import { OverlayPanel } from "primereact/overlaypanel";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { AutoComplete } from "primereact/autocomplete";
import { Tag } from "primereact/tag";
import { Tooltip } from "primereact/tooltip";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import PropTypes from "prop-types";
import { format, subYears, isValid } from "date-fns";

import { useAuth } from "context/AuthContext";
import exportAsCsv from "utils/exportAsCsv";
import PrimeReactDataGrid from "components/PrimeReactDataGrid/PrimeReactDataGrid";
import styles from "./RosterDetailsV2.module.css";
import {
  getMergeClassTraineeMilestoneDetails,
  getMilestoneByMergeRosterId,
  updateMergeClassRosterCurriculumStatus,
  getCurriculumStatus,
  getTraineeStatusList,
  getMergeClassRosterDetails,
  getSearchPopoverContent,
  updateMergeClassTraineeMilestoneGridDetails,
  bulkUpdateMergeClassTraineeMilestoneGridDetails,
  getSearchValue,
} from "../../../services/learnPointUserServices";
import BackdropComponent from "components/Spinner/Spinner";
import { useDispatch } from "react-redux";
import { SnackBarStoreAction } from "Store/SnackBar";
import SnackBar from "components/SnackBar/SnackBar";

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

const ALLOWED_ROLES = [
  "LD_Leader",
  "LD_Trainer",
  "LD_Group",
  "LD_Group_Admin",
  "Ops_Group",
];

const ONE_YEAR_AGO = subYears(new Date(), 1);

// Fields never sent to the save API — read-only by definition
const READ_ONLY_FIELDS = new Set([
  "id",
  "ClassRosterId",
  "ClassTraineeId",
  "TraineeOracleId",
  "TraineeName",
  "TraineeStatusId",
  "EmployeeStatus",
  "TraineeComment",
  "FTRR",
  "Project",
  "Site",
  "HasThirdPartyUpdate",
  "OperationalTeamLeadId",
]);

const INITIAL_FILTER_PROPS = {
  TraineeStatus: { value: "Active", matchMode: "equals" },
};

// ─────────────────────────────────────────────────────────────────────────────
// BULK EDIT — field descriptors for the multi-column bulk edit dialog.
// Static editable columns are listed here. Dynamic milestone columns are
// merged in at runtime from the `columnDefs` state.
// ─────────────────────────────────────────────────────────────────────────────

const BULK_EDITABLE_FIELDS = [
  { field: "TraineeStatus",           label: "Trainee Status",   type: "traineeStatus" },
  { field: "EndorsedToNestingStatus", label: "E2N",              type: "boolean"       },
  { field: "E2P",                     label: "E2P",              type: "boolean"       },
  { field: "OperationLeadName",       label: "Operation Lead",   type: "opsLead"       },
  { field: "ProductionDate",          label: "Production Date",  type: "date"          },
];

/** Returns the initial per-field state for the bulk dialog (all disabled, null values). */
const emptyBulkState = () =>
  Object.fromEntries(
    BULK_EDITABLE_FIELDS.map((f) => [f.field, { enabled: false, value: null }]),
  );

// ─────────────────────────────────────────────────────────────────────────────
// PURE UTILITIES
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Serialize a cell value to the string format the Merge update API expects.
 *   null / "" / undefined  →  "null"
 *   Array (empty)          →  "multiSelectNoValue"
 *   Array (filled)         →  comma-joined
 *   Date (valid)           →  "MM/dd/yyyy"
 *   anything else          →  .toString()
 */
const serializeValue = (value) => {
  if (value === null || value === undefined || value === "") return "null";
  if (typeof value === "string" && value.trim() === "") return "null";
  if (Array.isArray(value))
    return value.length > 0 ? value.join(",") : "multiSelectNoValue";
  if (value instanceof Date)
    return isValid(value) ? format(value, "MM/dd/yyyy") : "null";
  return value.toString();
};

/**
 * Parse the milestone type metadata string from getMilestoneByMergeRosterId.
 *   "multiselect-Good,Average,Poor"  →  { type: "multiselect", options: ["Good","Average","Poor"] }
 *   "date-NA"                        →  { type: "date", options: [] }
 */
const parseMilestoneType = (raw) => {
  const dashIdx = raw.indexOf("-");
  const type = raw.slice(0, dashIdx).trim().toLowerCase();
  const optionsPart = raw.slice(dashIdx + 1).trim();
  const options =
    optionsPart && optionsPart !== "NA" ? optionsPart.split(",") : [];
  return { type, options };
};

/** Debounce — arrow-safe, no `this` leak. */
const debounce = (fn, ms) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
};

/**
 * Filter "added in error" rows, attach stable `id`.
 * PrimeReact cell-edit tracking requires a `dataKey` — we use `id`.
 */
const normalizeRows = (data) =>
  data
    .filter((r) => r.TraineeStatus?.toLowerCase() !== "added in error")
    .map((r) => ({ ...r, id: r.ClassTraineeId }));

/**
 * Build the rosterToMilestones applicability map from getMilestoneByMergeRosterId data.
 * Returns { [rosterId: string]: Set<milestoneName: string> }
 */
const buildRosterMilestoneMap = (mergeData) => {
  const map = {};
  mergeData.forEach((rosterRow) => {
    const rosterId = String(rosterRow.ClassRosterId);
    if (!map[rosterId]) map[rosterId] = new Set();
    Object.entries(rosterRow).forEach(([key, value]) => {
      if (key !== "ClassRosterId" && value !== null && value !== undefined) {
        map[rosterId].add(key);
      }
    });
  });
  return map;
};

/**
 * Derive the union of all milestone column definitions across all rosters.
 * Returns [{ name, type, options }]
 */
const buildMilestoneColumnDefs = (mergeData) => {
  const seen = new Map(); // name → raw type string (first definition wins)
  mergeData.forEach((rosterRow) => {
    Object.entries(rosterRow).forEach(([key, value]) => {
      if (key !== "ClassRosterId" && value !== null && !seen.has(key)) {
        seen.set(key, value);
      }
    });
  });
  return Array.from(seen.entries()).map(([name, raw]) => ({
    name,
    ...parseMilestoneType(raw),
  }));
};

/**
 * Format multi-roster header values.
 * When multiple rosters are loaded, joins unique values; falls back to single-roster detail.
 */
const formatMultipleValues = (multipleRosterDetails, fieldName) => {
  if (!multipleRosterDetails || multipleRosterDetails.length === 0) return "—";
  const accessor = {
    classRosterId: (r) => r.ClassRosterId,
    className: (r) => r.ClassName,
    project: (r) => r.Project,
  }[fieldName];
  if (!accessor) return "—";
  const unique = [
    ...new Set(multipleRosterDetails.map(accessor).filter(Boolean)),
  ];
  return unique.length > 0 ? unique.join(", ") : "—";
};

// ─────────────────────────────────────────────────────────────────────────────
// EDITOR SUBCOMPONENTS
// Defined at module scope — not re-registered on every render.
// All editors are copied from ClassRosterGridV2 without modification,
// except those that receive an `applicable` prop for milestone grey-out.
// ─────────────────────────────────────────────────────────────────────────────

/** Memo (multiline) editor — with optional not-applicable guard */
const MEMO_MAX_LENGTH = 4000;

const MemoEditor = ({ value, editorCallback, applicable = true }) => {
  if (!applicable) return null;
  return (
    <div className={styles.memoEditor}>
      <InputTextarea
        value={value || ""}
        onChange={(e) => editorCallback(e.target.value)}
        rows={2}
        maxLength={MEMO_MAX_LENGTH}
        autoResize
        className={styles.memoTextarea}
      />
      <span
        className={`${styles.charCount} ${
          (value?.length ?? 0) >= MEMO_MAX_LENGTH ? styles.charLimit : ""
        }`}
      >
        {value?.length ?? 0}/{MEMO_MAX_LENGTH}
      </span>
    </div>
  );
};

MemoEditor.propTypes = {
  value: PropTypes.string,
  editorCallback: PropTypes.func.isRequired,
  applicable: PropTypes.bool,
};

/**
 * Date picker — self-contained validation, hard lower bound of ONE_YEAR_AGO.
 * Returns null (blocking edit) when not applicable to the row's roster.
 */
const DateEditor = ({ value, editorCallback, applicable = true }) => {
  const [error, setError] = useState(false);
  if (!applicable) return null;

  const parsed = value && value !== "null" ? new Date(value) : null;

  const handleChange = (e) => {
    const d = e.value;
    if (d === null) {
      setError(false);
      editorCallback(null);
      return;
    }
    if (d < ONE_YEAR_AGO) {
      setError(true);
      // Do NOT call editorCallback — leave committed value unchanged
    } else {
      setError(false);
      editorCallback(d);
    }
  };

  return (
    <div className={styles.dateEditorWrapper}>
      <Calendar
        value={parsed}
        onChange={handleChange}
        dateFormat="mm/dd/yy"
        showButtonBar
        minDate={ONE_YEAR_AGO}
        showIcon
        inputClassName="rg-date-input"
        panelClassName="rg-date-panel"
      />
      {error && (
        <small className={styles.dateError}>Cannot be older than 1 year</small>
      )}
    </div>
  );
};

DateEditor.propTypes = {
  value: PropTypes.string,
  editorCallback: PropTypes.func.isRequired,
  applicable: PropTypes.bool,
};

/** Chip-based multiselect editor */
const MultiSelectEditor = ({
  value,
  options,
  editorCallback,
  applicable = true,
}) => {
  if (!applicable) return null;

  const selected = Array.isArray(value)
    ? value
    : value
      ? value
          .split(",")
          .map((v) => v.trim())
          .filter(Boolean)
      : [];

  return (
    <MultiSelect
      value={selected}
      options={options}
      onChange={(e) => editorCallback(e.value)}
      display="chip"
      placeholder="Select…"
      className={styles.multiselect}
      panelClassName="rg-multiselect-panel"
    />
  );
};

MultiSelectEditor.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  options: PropTypes.array.isRequired,
  editorCallback: PropTypes.func.isRequired,
  applicable: PropTypes.bool,
};

/** Single-select dropdown */
const SingleSelectEditor = ({
  value,
  options,
  editorCallback,
  applicable = true,
}) => {
  if (!applicable) return null;
  return (
    <Dropdown
      value={value}
      options={options}
      onChange={(e) => editorCallback(e.value)}
      placeholder="Select…"
      className={styles.dropdown}
      panelClassName="rg-dropdown-panel"
    />
  );
};

SingleSelectEditor.propTypes = {
  value: PropTypes.string,
  options: PropTypes.array.isRequired,
  editorCallback: PropTypes.func.isRequired,
  applicable: PropTypes.bool,
};

/**
 * TraineeStatus editor — native <select> operating in numeric TraineeStatusId space.
 * Value contract identical to ClassRosterGridV2.
 *   opt.disabled: true  = role CAN select
 *   opt.disabled: false = role CANNOT select → HTML disabled attr
 */
const TraineeStatusEditor = ({ value, options, editorCallback }) => {
  const [selected, setSelected] = useState(value != null ? String(value) : "");

  const handleChange = (e) => {
    const raw = e.target.value;
    setSelected(raw);
    editorCallback(parseInt(raw, 10));
  };

  return (
    <select
      value={selected}
      onChange={handleChange}
      className={styles.nativeStatusSelect}
    >
      <option value="" disabled>
        Select status…
      </option>
      {options.map((opt) => (
        <option
          key={opt.value}
          value={String(opt.value)}
          disabled={!opt.disabled}
        >
          {opt.label}
        </option>
      ))}
    </select>
  );
};

TraineeStatusEditor.propTypes = {
  value: PropTypes.number,
  options: PropTypes.array.isRequired,
  editorCallback: PropTypes.func.isRequired,
};

/**
 * OpsLead editor — PrimeReact AutoComplete with inline debounced search.
 * Calls onPersonSelected when a result is picked so the parent can stash
 * the OracleId (not surfaced via editorCallback's string).
 */
const OpsLeadEditor = ({ rowData, editorCallback, onPersonSelected }) => {
  const [query, setQuery] = useState(rowData.OperationLeadName || "");
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce(async (q) => {
      if (!q || q.length < 3) return;
      setSearching(true);
      try {
        const res = await getSearchValue(q);
        if (res?.message === "Success") {
          setSuggestions(res.data.filter((i) => i.Email?.length > 0));
        }
      } finally {
        setSearching(false);
      }
    }, 300),
    [],
  );

  const handleSelect = (e) => {
    const person = e.value;
    const display = `${person.Name} (${person.Email})`;
    setQuery(display);
    editorCallback(display);
    onPersonSelected(person);
    setSuggestions([]);
  };

  return (
    <span
      className={`${styles.opsLeadEditor}${
        searching ? ` ${styles.opsLeadEditorSearching}` : ""
      }`}
    >
      <AutoComplete
        value={query}
        suggestions={suggestions}
        field="Name"
        itemTemplate={(item) => (
          <div className={styles.opsSuggestion}>
            <span className={styles.opsName}>{item.Name}</span>
            <span className={styles.opsEmail}>{item.Email}</span>
          </div>
        )}
        completeMethod={(e) => {
          setQuery(e.query);
          debouncedSearch(e.query);
        }}
        onSelect={handleSelect}
        onChange={(e) => {
          setQuery(
            typeof e.value === "string" ? e.value : (e.value?.Name ?? ""),
          );
        }}
        placeholder="Type 3+ chars to search…"
        className={styles.opsAutocomplete}
        inputClassName={styles.opsInput}
        minLength={3}
        delay={0}
        forceSelection={false}
        dropdown={false}
      />
      {searching && (
        <span className={styles.opsSpinner}>
          <CircularProgress size={13} thickness={5} sx={{ color: "#64748b" }} />
        </span>
      )}
    </span>
  );
};

OpsLeadEditor.propTypes = {
  rowData: PropTypes.object.isRequired,
  editorCallback: PropTypes.func.isRequired,
  onPersonSelected: PropTypes.func.isRequired,
};

// ─────────────────────────────────────────────────────────────────────────────
// BODY / DISPLAY TEMPLATES
// ─────────────────────────────────────────────────────────────────────────────

const OracleIdTemplate = ({ rowData, onInfoClick }) => (
  <span className={styles.oracleCell}>
    {rowData.HasThirdPartyUpdate && (
      // ASTT-7319: raw <span onClick> -- not keyboard-focusable, no
      // accessible name, no button semantics. role/tabIndex/onKeyDown make
      // this a real, operable control; aria-label states what it does
      // (previously conveyed only via the PrimeReact tooltip, which
      // doesn't substitute for an accessible name -- same gap fixed
      // earlier in Attendance.js and RichTextEditorField.js).
      <span
        className={styles.infoIconAnchor}
        data-pr-tooltip="Click for 3rd-party tracking info"
        data-pr-position="top"
        role="button"
        tabIndex={0}
        aria-label="3rd-party tracking info"
        onClick={(e) => onInfoClick(e, rowData)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onInfoClick(e, rowData);
          }
        }}
      >
        <InfoOutlinedIcon sx={{ color: "#3299ff" }} />
      </span>
    )}
    {rowData.TraineeOracleId}
  </span>
);

const TraineeNameTemplate = ({ rowData, onInfoClick }) => (
  // ASTT-7319: same gap as OracleIdTemplate above -- role/tabIndex/
  // onKeyDown/aria-label only added when actually clickable
  // (HasThirdPartyUpdate), matching the existing conditional onClick.
  <span
    className={`${styles.traineeName}${
      rowData.HasThirdPartyUpdate ? ` ${styles.traineeNameHasUpdate}` : ""
    }`}
    role={rowData.HasThirdPartyUpdate ? "button" : undefined}
    tabIndex={rowData.HasThirdPartyUpdate ? 0 : undefined}
    aria-label={
      rowData.HasThirdPartyUpdate ? "3rd-party tracking info" : undefined
    }
    onClick={
      rowData.HasThirdPartyUpdate ? (e) => onInfoClick(e, rowData) : undefined
    }
    onKeyDown={
      rowData.HasThirdPartyUpdate
        ? (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onInfoClick(e, rowData);
            }
          }
        : undefined
    }
  >
    {rowData.TraineeName}
  </span>
);

const TraineeStatusBodyTemplate = ({ rowData, traineeOptions }) => {
  const opt = traineeOptions.find((o) => o.value === rowData.TraineeStatusId);
  const label = opt?.label ?? rowData.TraineeStatus ?? "";
  const severity =
    label.toLowerCase() === "active"
      ? "success"
      : label.toLowerCase().includes("pending")
        ? "warning"
        : "secondary";
  return <Tag value={label} severity={severity} className="rg-status-tag" />;
};

const BooleanIconTemplate = ({ value }) =>
  value ? (
    <CheckCircleOutlinedIcon className={styles.ftrrIconTrue} />
  ) : (
    <CancelOutlinedIcon className={styles.ftrrIconFalse} />
  );

BooleanIconTemplate.propTypes = {
  value: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
};

const FtrrReadonlyCell = ({ value }) => (
  <span className={styles.ftrrReadonly}>
    {value ? (
      <CheckCircleOutlinedIcon className={styles.ftrrIconTrue} />
    ) : (
      <CancelOutlinedIcon className={styles.ftrrIconFalse} />
    )}
  </span>
);

FtrrReadonlyCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
};

const DateBodyTemplate = ({ value }) => {
  if (!value || value === "null")
    return <span className={styles.emptyCell}>—</span>;
  try {
    return <span>{format(new Date(value), "MM/dd/yyyy")}</span>;
  } catch {
    return <span className={styles.emptyCell}>—</span>;
  }
};

const MultiSelectBodyTemplate = ({ value }) => {
  const [expanded, setExpanded] = useState(false);

  if (!value) return <span className={styles.emptyCell}>—</span>;
  const items = Array.isArray(value)
    ? value
    : value
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
  if (!items.length) return <span className={styles.emptyCell}>—</span>;

  const LIMIT = 2;
  const overflow = items.length - LIMIT;
  const visible = expanded ? items : items.slice(0, LIMIT);

  return (
    <span className={styles.chipRow}>
      {visible.map((v) => (
        <Tag key={v} value={v} className="rg-chip" />
      ))}
      {/* ASTT-7319: PrimeReact's Tag is a display/badge component with no
          built-in keyboard operability or button semantics -- attaching
          onClick directly to it (as before) left this "show more" toggle
          unreachable by keyboard. Wrapping in a plain span I have full
          control over is safer than adding role/tabIndex/onKeyDown
          directly to Tag, since I can't fully confirm Tag forwards
          arbitrary props through to its underlying DOM element. */}
      {!expanded && overflow > 0 && (
        <span
          role="button"
          tabIndex={0}
          aria-label={`Show ${overflow} more`}
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
              setExpanded(true);
            }
          }}
        >
          <Tag value={`+${overflow}`} className="rg-chip rg-chip-more rg-chip-toggle" />
        </span>
      )}
      {expanded && (
        <span
          role="button"
          tabIndex={0}
          aria-label="Show fewer"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(false);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
              setExpanded(false);
            }
          }}
        >
          <Tag value="−" className="rg-chip rg-chip-more rg-chip-toggle" />
        </span>
      )}
    </span>
  );
};

/** Grey cell for milestones not applicable to a trainee's roster */
const NotApplicableCell = () => <span className={styles.notApplicable}>—</span>;

// ─────────────────────────────────────────────────────────────────────────────
// BULK EDIT — MODULE-LEVEL SUB-COMPONENTS
//
// Defined at module level so React never sees a new component type between
// renders. Inline `const` inside the render body causes unmount/remount on
// every state change → focus loss after every keystroke.
// ─────────────────────────────────────────────────────────────────────────────

// ASTT-7319: RD_BulkOpsLeadInput -> RDBulkOpsLeadInput (underscore removed,
// "RD" prefix kept as a meaningful RosterDetails-scoped marker) -- fixes a
// real, already-flagged react/jsx-pascal-case ESLint warning tied to this
// exact file (confirmed from an earlier npm run build log).
const RDBulkOpsLeadInput = ({ value, onChange, onPersonSelect, styles: s }) => {
  const [query, setQuery] = useState(value ?? "");
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce(async (q) => {
      if (!q || q.length < 3) { setSuggestions([]); return; }
      setSearching(true);
      try {
        const res = await getSearchValue(q);
        if (res?.message === "Success") {
          setSuggestions(res.data.filter((i) => i.Email?.length > 0));
        }
      } finally {
        setSearching(false);
      }
    }, 300),
    [],
  );

  const handleChange = (e) => {
    const q = typeof e.value === "string" ? e.value : (e.value?.Name ?? "");
    setQuery(q);
    onChange(q);
    debouncedSearch(q);
  };

  const handleSelect = (e) => {
    const person = e.value;
    const display = `${person.Name} (${person.Email})`;
    setQuery(display);
    onChange(display);         // display string — for visual feedback only
    onPersonSelect(person.OracleId); // OracleId — what actually goes to the API
    setSuggestions([]);
  };

  return (
    <span className={s.bulkOpsLeadWrap}>
      <AutoComplete
        value={query}
        suggestions={suggestions}
        field="Name"
        itemTemplate={(item) => (
          <div className={s.opsSuggestion}>
            <span className={s.opsName}>{item.Name}</span>
            <span className={s.opsEmail}>{item.Email}</span>
          </div>
        )}
        completeMethod={(e) => debouncedSearch(e.query)}
        onSelect={handleSelect}
        onChange={handleChange}
        placeholder="Type 3+ chars to search…"
        inputClassName={s.bulkTextInput}
        minLength={3}
        delay={0}
        forceSelection={false}
        dropdown={false}
      />
      {searching && (
        <span className={s.opsSpinner}>
          <CircularProgress size={13} thickness={5} sx={{ color: "#64748b" }} />
        </span>
      )}
    </span>
  );
};

RDBulkOpsLeadInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onPersonSelect: PropTypes.func.isRequired,
  styles: PropTypes.object.isRequired,
};

// ASTT-7319: same rename as RDBulkOpsLeadInput above -- see that comment.
const RDBulkFieldRow = ({
  fieldDef,
  enabled,
  value,
  onToggle,
  onValueChange,
  onOpsLeadSelect,
  traineeOptions,
  styles: s,
  minDate,
}) => {
  const { field, label, type, options = [] } = fieldDef;

  return (
    <div className={s.bulkFieldRow}>
      <Checkbox
        checked={enabled}
        onChange={(e) => onToggle(e.checked)}
        inputId={`bulk-chk-${field}`}
      />
      <label
        htmlFor={`bulk-chk-${field}`}
        className={`${s.bulkFieldLabel}${enabled ? ` ${s.bulkFieldLabelActive}` : ""}`}
        title={label}
      >
        {label}
      </label>
      <div
        className={s.bulkFieldInput}
        style={{ opacity: enabled ? 1 : 0.35, pointerEvents: enabled ? "auto" : "none" }}
      >
        {type === "traineeStatus" && (
          <select
            value={value != null ? String(value) : ""}
            onChange={(e) => {
              const raw = e.target.value;
              onValueChange(raw === "" ? null : parseInt(raw, 10));
            }}
            className={s.nativeStatusSelect}
          >
            <option value="" disabled>Select status…</option>
            {traineeOptions.map((opt) => (
              <option
                key={opt.value}
                value={String(opt.value)}
                disabled={!opt.disabled}
              >
                {opt.label}
              </option>
            ))}
          </select>
        )}
        {type === "boolean" && (
          <Dropdown
            value={value}
            options={[
              { label: "Yes", value: true },
              { label: "No",  value: false },
            ]}
            optionLabel="label"
            optionValue="value"
            onChange={(e) => onValueChange(e.value)}
            placeholder="Select…"
            className={s.bulkDropdown}
            panelClassName="rg-dropdown-panel"
          />
        )}
        {type === "date" && (
          <Calendar
            value={value}
            onChange={(e) => onValueChange(e.value)}
            dateFormat="mm/dd/yy"
            showButtonBar
            minDate={minDate}
            showIcon
            inputClassName="rg-date-input"
            panelClassName="rg-date-panel"
            className={s.bulkCalendar}
          />
        )}
        {type === "opsLead" && (
          <RDBulkOpsLeadInput
            value={value ?? ""}
            onChange={onValueChange}
            onPersonSelect={onOpsLeadSelect}
            styles={s}
          />
        )}
        {(type === "text" || type === "default") && (
          <InputText
            value={value ?? ""}
            onChange={(e) => onValueChange(e.target.value)}
            placeholder={`Enter ${label.toLowerCase()}…`}
            className={s.bulkTextInput}
          />
        )}
        {(type === "memo" || type === "textarea") && (
          <div className={s.bulkMemoWrap}>
            <InputTextarea
              value={value ?? ""}
              onChange={(e) => onValueChange(e.target.value)}
              placeholder={`Enter ${label.toLowerCase()}…`}
              className={s.bulkTextInput}
              rows={2}
              maxLength={MEMO_MAX_LENGTH}
              autoResize
            />
            <span
              className={`${s.bulkMemoCount}${
                (value?.length ?? 0) >= MEMO_MAX_LENGTH ? ` ${s.bulkMemoCountLimit}` : ""
              }`}
            >
              {value?.length ?? 0}/{MEMO_MAX_LENGTH}
            </span>
          </div>
        )}
        {type === "number" && (
          <InputNumber
            value={value ?? null}
            onValueChange={(e) => onValueChange(e.value)}
            useGrouping={false}
            allowEmpty
            placeholder={`Enter ${label.toLowerCase()}…`}
            className={s.bulkTextInput}
            inputClassName={s.bulkTextInput}
          />
        )}
        {type === "singleselect" && (
          <Dropdown
            value={value}
            options={options}
            onChange={(e) => onValueChange(e.value)}
            placeholder="Select…"
            className={s.bulkDropdown}
            panelClassName="rg-dropdown-panel"
          />
        )}
        {type === "multiselect" && (
          <MultiSelect
            value={Array.isArray(value) ? value : []}
            options={options}
            onChange={(e) => onValueChange(e.value)}
            display="chip"
            placeholder="Select…"
            className={s.bulkDropdown}
            panelClassName="rg-multiselect-panel"
          />
        )}
        {![
          "traineeStatus", "boolean", "date", "opsLead",
          "text", "default", "memo", "textarea",
          "number", "singleselect", "multiselect",
        ].includes(type) && (
          <InputText
            value={value ?? ""}
            onChange={(e) => onValueChange(e.target.value)}
            placeholder={`Enter ${label.toLowerCase()}…`}
            className={s.bulkTextInput}
          />
        )}
      </div>
    </div>
  );
};

RDBulkFieldRow.propTypes = {
  fieldDef:        PropTypes.object.isRequired,
  enabled:         PropTypes.bool.isRequired,
  value:           PropTypes.any,
  onToggle:        PropTypes.func.isRequired,
  onValueChange:   PropTypes.func.isRequired,
  onOpsLeadSelect: PropTypes.func.isRequired,
  traineeOptions:  PropTypes.array.isRequired,
  styles:          PropTypes.object.isRequired,
  minDate:         PropTypes.instanceOf(Date),
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

const RosterDetailsV2 = ({ classRosterId }) => {
  const { roles } = useAuth();

  // ── Metadata ───────────────────────────────────────────────────────────────
  const [multipleRosterDetails, setMultipleRosterDetails] = useState([]);
  const [curriculumOptions, setCurriculumOptions] = useState([]);
  const [curriculumValue, setCurriculumValue] = useState(null);
  const [traineeOptions, setTraineeOptions] = useState([]);

  // ── Grid data ──────────────────────────────────────────────────────────────
  const [rows, setRows] = useState([]);
  const [columnDefs, setColumnDefs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataReady, setDataReady] = useState(false);

  // ── Bulk edit state ──────────────────────────────────────────────────────
  const [selectedRows, setSelectedRows] = useState([]);
  const [bulkDialogVisible, setBulkDialogVisible] = useState(false);
  const [bulkFieldState, setBulkFieldState] = useState(emptyBulkState);
  const [bulkSaving, setBulkSaving] = useState(false);

  /**
   * rosterMilestoneMapRef — built once per load from getMilestoneByMergeRosterId.
   * Shape: { [rosterId: string]: Set<milestoneName> }
   * Stored as a ref so column defs (useMemo) can close over it without
   * triggering re-memoization on every render.
   */
  const rosterMilestoneMapRef = useRef({});

  /**
   * pendingOpsLeadRef — holds the selected person object from OpsLeadEditor
   * until onCellEditComplete fires for OperationLeadName.
   * Shape: { rowId: number, person: { OracleId, Name, Email } } | null
   */
  const pendingOpsLeadRef = useRef(null);

  // ── Bulk ops lead OracleId ref ────────────────────────────────────────────
  // Same pattern as ClassRosterGridV2. bulkFieldState stores the display
  // string for visual feedback; this ref is what applyBulkEdit serializes.
  const bulkOpsLeadOracleIdRef = useRef(null);

  // ── Overlay ────────────────────────────────────────────────────────────────
  const overlayRef = useRef(null);
  const [popoverHtml, setPopoverHtml] = useState("");

  const dispatch = useDispatch();

  // ── Snack ──────────────────────────────────────────────────────────────────
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const snackReset = () => setSnack({ open: false, message: "", severity: "" });

  const showSnack = useCallback(
    (message, severity) => {
      dispatch(
        SnackBarStoreAction.updateSnack({ open: true, message, severity }),
      );
    },
    [dispatch],
  );

  // ── Applicability helper — reads from ref, no state dependency ─────────────
  const isMilestoneApplicable = useCallback(
    (traineeRosterId, milestoneName) => {
      return (
        rosterMilestoneMapRef.current[String(traineeRosterId)]?.has(
          milestoneName,
        ) ?? false
      );
    },
    [],
  );

  // ── Role check ─────────────────────────────────────────────────────────────
  const checkRole = useCallback(
    (role) => ALLOWED_ROLES.includes(role) && roles.includes(role),
    [roles],
  );

  // ─────────────────────────────────────────────────────────────────────────
  // DATA LOADING
  // Single consolidated function — replaces the three duplicate useEffects.
  // ─────────────────────────────────────────────────────────────────────────

  const loadAll = useCallback(async () => {
    if (!classRosterId) return;
    setLoading(true);
    setDataReady(false);

    try {
      // ── Trainee status options ───────────────────────────────────────────
      let traineeOpts = [];
      try {
        const traineeRes = await getTraineeStatusList();
        if (traineeRes?.message === "Success") {
          traineeOpts = traineeRes.data.map((item) => {
            const label = item.label.toLowerCase();
            const isAdmin = checkRole("LD_Group_Admin");
            const isTrainer = checkRole("LD_Trainer") || checkRole("LD_Group");
            const isLeader = checkRole("LD_Leader");
            const isOpsGroup = checkRole("Ops_Group");
            const disabled =
              (label === "active" && isAdmin) ||
              (label === "added in error" && isAdmin) ||
              (label === "pending r/t" &&
                (isAdmin || isOpsGroup || isTrainer || isLeader));
            return {
              ...item,
              disabled,
              isDisabled: !disabled,
            };
          });
        }
      } catch {
        /* non-fatal */
      }
      setTraineeOptions(traineeOpts);

      // ── Curriculum status options ────────────────────────────────────────
      try {
        const currRes = await getCurriculumStatus();
        if (currRes?.message === "Success") {
          setCurriculumOptions(currRes.data);
        }
      } catch {
        /* non-fatal */
      }

      // ── Roster header details (multi-roster aware) ───────────────────────
      try {
        const rosterRes = await getMergeClassRosterDetails(classRosterId);
        if (rosterRes?.message === "Success") {
          const rosterData = rosterRes.data ?? [];
          setMultipleRosterDetails(rosterData);
        }
      } catch {
        /* non-fatal */
      }

      // ── Milestone column definitions (merge union) ───────────────────────
      try {
        const milRes = await getMilestoneByMergeRosterId(classRosterId);
        if (milRes?.message === "Success" && milRes.data?.length > 0) {
          rosterMilestoneMapRef.current = buildRosterMilestoneMap(milRes.data);
          setColumnDefs(buildMilestoneColumnDefs(milRes.data));
        }
      } catch {
        /* non-fatal — no dynamic columns */
      }

      // ── Grid rows ────────────────────────────────────────────────────────
      const gridRes = await getMergeClassTraineeMilestoneDetails(classRosterId);
      if (gridRes?.message === "Success") {
        const allRows = Object.values(gridRes.data).map((item) => ({
          ...item,
          id: item.ClassTraineeId,
        }));

        const filtered = allRows.filter(
          (r) => r.TraineeStatus?.toLowerCase() !== "added in error",
        );

        // Warn on roster IDs with no data
        const loadedRosterIds = new Set(
          allRows.map((r) => String(r.ClassRosterId)),
        );
        const requestedIds = String(classRosterId)
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
        const missing = requestedIds.filter((id) => !loadedRosterIds.has(id));
        if (missing.length > 0) {
          showSnack(
            `No trainee data found for Roster ID(s): ${missing.join(", ")}`,
            "warning",
          );
        } else if (filtered.length === 0) {
          showSnack(
            "No trainee records found for the selected roster(s).",
            "warning",
          );
        }

        setRows(filtered);
      } else {
        showSnack(`Records not found for Roster ID: ${classRosterId}`, "error");
      }

      setDataReady(true);
    } catch {
      showSnack("An error occurred while loading roster data.", "error");
    } finally {
      setLoading(false);
    }
  }, [classRosterId, checkRole, showSnack]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // ── Curriculum status consensus value ──────────────────────────────────────
  // Derived whenever multipleRosterDetails or curriculumOptions changes.
  // null if rosters disagree; resolved to the matching option if they agree.
  useEffect(() => {
    if (!multipleRosterDetails || multipleRosterDetails.length === 0) {
      setCurriculumValue(null);
      return;
    }
    const first = multipleRosterDetails[0].CurriculumStatusId;
    const allSame = multipleRosterDetails.every(
      (r) => r.CurriculumStatusId === first,
    );
    if (!allSame || first === null || first === undefined) {
      setCurriculumValue(null);
      return;
    }
    const id = Number(first);
    const option = curriculumOptions.find(
      (o) => Number(o.LP_CurriculumStatusId) === id,
    );
    if (option) {
      setCurriculumValue(option);
    } else {
      const rosterMatch = multipleRosterDetails.find(
        (r) => r.CurriculumStatusId === first,
      );
      setCurriculumValue({
        LP_CurriculumStatusId: id,
        CurriculumStatus: rosterMatch?.CurriculumStatus || "",
      });
    }
  }, [multipleRosterDetails, curriculumOptions]);

  // ─────────────────────────────────────────────────────────────────────────
  // CURRICULUM STATUS UPDATE
  // ─────────────────────────────────────────────────────────────────────────

  const handleCurriculumChange = async (selected) => {
    if (!selected) return;
    const previous = curriculumValue;
    setCurriculumValue(selected);
    try {
      // POST all loaded roster IDs as a comma string
      const rosterIds =
        multipleRosterDetails.length > 0
          ? multipleRosterDetails.map((r) => r.ClassRosterId).join(",")
          : classRosterId;

      const res = await updateMergeClassRosterCurriculumStatus(
        rosterIds,
        selected.LP_CurriculumStatusId,
      );
      if (res?.message === "Success") {
        const count =
          typeof rosterIds === "string"
            ? rosterIds.split(",").filter(Boolean).length
            : 1;
        showSnack(
          count > 1
            ? `Curriculum status updated for all ${count} rosters.`
            : "Curriculum status updated.",
          "success",
        );
        // Keep multipleRosterDetails in sync so the consensus effect resolves correctly
        setMultipleRosterDetails((prev) =>
          prev.map((r) => ({
            ...r,
            CurriculumStatusId: selected.LP_CurriculumStatusId,
            CurriculumStatus: selected.CurriculumStatus,
          })),
        );
      } else {
        setCurriculumValue(previous);
        showSnack("Could not update curriculum status.", "error");
      }
    } catch {
      setCurriculumValue(previous);
      showSnack("An internal error occurred.", "error");
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // BULK EDIT — field-state helpers + sequential apply handler
  //
  // Identical contract to ClassRosterGridV2's applyBulkEdit but calls the
  // Merge API (updateMergeClassTraineeMilestoneGridDetails) and also
  // re-checks milestone applicability guard for dynamic columns.
  // ─────────────────────────────────────────────────────────────────────────

  const setBulkField = useCallback((field, key, val) => {
    setBulkFieldState((prev) => ({
      ...prev,
      [field]: { ...prev[field], [key]: val },
    }));
  }, []);

  const allBulkFields = useMemo(
    () => [
      ...BULK_EDITABLE_FIELDS,
      ...columnDefs.map((col) => ({
        field: col.name,
        label: col.name,
        type: col.type,
        options: col.options,
        isDynamic: true,
      })),
    ],
    [columnDefs],
  );

  // Milestone columns to show in the bulk dialog — filtered to only those
  // applicable to at least one of the currently selected rows' rosters.
  //
  // Rationale: columnDefs holds the UNION of all milestones across all loaded
  // rosters. If the user selected only R1 trainees they should not see R2-only
  // milestones in the dialog; it is confusing and misleading. We compute the
  // applicable set from rosterMilestoneMapRef using the unique set of
  // ClassRosterIds present in selectedRows.
  const dialogMilestoneColumnDefs = useMemo(() => {
    if (selectedRows.length === 0) return columnDefs; // nothing selected — show all
    const selectedRosterIds = [
      ...new Set(selectedRows.map((r) => String(r.ClassRosterId))),
    ];
    // Milestone is shown if it belongs to at least one selected roster.
    return columnDefs.filter((col) =>
      selectedRosterIds.some(
        (id) => rosterMilestoneMapRef.current[id]?.has(col.name),
      ),
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRows, columnDefs]);

  const activeFields = allBulkFields.filter(
    (f) => bulkFieldState[f.field]?.enabled,
  );

  const closeBulkDialog = useCallback(() => {
    setBulkDialogVisible(false);
    setBulkFieldState(emptyBulkState());
    bulkOpsLeadOracleIdRef.current = null;
  }, []);

  const applyBulkEdit = useCallback(async () => {
    if (activeFields.length === 0) {
      showSnack("Enable at least one field to update.", "warning");
      return;
    }
    for (const f of activeFields) {
      const val = bulkFieldState[f.field]?.value;
      if (f.type === "boolean") {
        if (val === null || val === undefined) {
          showSnack(`Please select Yes or No for "${f.label}" before applying.`, "warning");
          return;
        }
        continue;
      }
      if (f.type === "multiselect") {
        const arr = Array.isArray(val) ? val : [];
        if (arr.length === 0) {
          showSnack(`Please select at least one value for "${f.label}" before applying.`, "warning");
          return;
        }
        continue;
      }
      if (val === null || val === undefined || val === "") {
        showSnack(`Please set a value for "${f.label}" before applying.`, "warning");
        return;
      }
      if ((f.type === "memo" || f.type === "textarea") && val.length > MEMO_MAX_LENGTH) {
        showSnack(
          `"${f.label}" exceeds ${MEMO_MAX_LENGTH} characters (${val.length}). Please shorten it.`,
          "warning",
        );
        return;
      }
    }

    setBulkSaving(true);
    setLoading(true);

    // ── Build flat updates array: one entry per (row × field). ────────────
    // Dynamic milestone columns are skipped for trainees whose roster does
    // not include that milestone (mirrors the onCellEditComplete guard).
    const isDynamicField = (fieldName) => columnDefs.some((c) => c.name === fieldName);

    const updates = [];
    for (const row of selectedRows) {
      for (const f of activeFields) {
        // Applicability guard: skip dynamic columns not belonging to this trainee's roster
        if (isDynamicField(f.field) && !isMilestoneApplicable(row.ClassRosterId, f.field)) {
          continue;
        }

        const rawValue = bulkFieldState[f.field]?.value;
        let serialized;
        if (f.field === "OperationLeadName") {
          // Serialize the OracleId captured at selection time, not the display string.
          const oracleId = bulkOpsLeadOracleIdRef.current;
          if (!oracleId) {
            showSnack('Please select a valid person for "Operation Lead".', "warning");
            setBulkSaving(false);
            setLoading(false);
            return;
          }
          serialized = serializeValue(oracleId);
        } else if (f.field === "TraineeStatus") {
          serialized = serializeValue(rawValue);
        } else if (f.type === "boolean") {
          serialized = serializeValue(rawValue === true || rawValue === 1);
        } else {
          serialized = serializeValue(rawValue);
        }
        updates.push({
          classTraineeId: row.id,
          fieldToUpdate:  f.field,
          valueToUpdate:  serialized,
        });
      }
    }

    if (updates.length === 0) {
      showSnack("No applicable updates to apply for the selected rows and fields.", "warning");
      setBulkSaving(false);
      setLoading(false);
      return;
    }

    // ── Optimistic update: apply all patches immediately. ─────────────────
    // Same applicability guard as the updates array — don't patch milestone
    // fields onto trainees whose roster doesn't own that milestone.
    const rowOptimisticPatches = {};
    for (const row of selectedRows) {
      const patch = {};
      for (const f of activeFields) {
        if (isDynamicField(f.field) && !isMilestoneApplicable(row.ClassRosterId, f.field)) {
          continue;
        }
        const rawValue = bulkFieldState[f.field]?.value;
        if (f.field === "TraineeStatus") {
          const matchedOpt = traineeOptions.find((o) => o.value === rawValue);
          patch.TraineeStatusId = rawValue;
          patch.TraineeStatus   = matchedOpt?.label ?? row.TraineeStatus;
        } else if (f.type === "boolean") {
          patch[f.field] = rawValue === true || rawValue === 1;
        } else {
          patch[f.field] = rawValue;
        }
      }
      rowOptimisticPatches[row.id] = patch;
    }
    setRows((prev) =>
      prev.map((r) =>
        rowOptimisticPatches[r.id] ? { ...r, ...rowOptimisticPatches[r.id] } : r,
      ),
    );

    try {
      const res = await bulkUpdateMergeClassTraineeMilestoneGridDetails(
        classRosterId,
        updates,
      );

      if (res?.message === "Success" || res?.message === "NotFound") {
        showSnack(
          `Bulk update applied: ${selectedRows.length} row(s) × ${activeFields.length} field(s) updated.`,
          "success",
        );
        if (Array.isArray(res?.data) && res.data.length > 0) {
          setRows(normalizeRows(res.data));
        }
      } else {
        setRows((prev) =>
          prev.map((r) => {
            const original = selectedRows.find((s) => s.id === r.id);
            return original ? { ...original } : r;
          }),
        );
        showSnack("Bulk update failed. No changes were saved.", "error");
      }
    } catch {
      setRows((prev) =>
        prev.map((r) => {
          const original = selectedRows.find((s) => s.id === r.id);
          return original ? { ...original } : r;
        }),
      );
      showSnack("An error occurred during bulk update.", "error");
    } finally {
      setBulkSaving(false);
      setLoading(false);
      setBulkDialogVisible(false);
      setBulkFieldState(emptyBulkState());
      setSelectedRows([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFields, bulkFieldState, classRosterId, columnDefs, isMilestoneApplicable, selectedRows, showSnack, traineeOptions]);

  // ─────────────────────────────────────────────────────────────────────────
  // THIRD-PARTY TRACKING OVERLAY
  // ─────────────────────────────────────────────────────────────────────────

  const handleInfoClick = useCallback(
    async (e, rowData) => {
      if (!rowData.HasThirdPartyUpdate) return;
      const anchorEl = e.currentTarget ?? e.nativeEvent.target;
      try {
        const res = await getSearchPopoverContent(rowData.ClassTraineeId);
        if (res?.message === "Success") {
          setPopoverHtml(res.data[0]?.TrackingDetails ?? "");
          overlayRef.current?.toggle({ currentTarget: anchorEl });
        }
      } catch {
        showSnack("Could not load tracking details.", "error");
      }
    },
    [showSnack],
  );

  // ─────────────────────────────────────────────────────────────────────────
  // EXPORT
  // ─────────────────────────────────────────────────────────────────────────

  const handleExport = useCallback(() => {
    const exportCols = [
      { field: "ClassRosterId", headerName: "Roster ID" },
      { field: "TraineeOracleId", headerName: "Oracle Id" },
      { field: "TraineeName", headerName: "Trainee Name" },
      { field: "TraineeStatus", headerName: "Trainee Status" },
      { field: "EmployeeStatus", headerName: "Employee Status" },
      { field: "TraineeComment", headerName: "Comment" },
      { field: "FTRR", headerName: "FTRR" },
      { field: "EndorsedToNestingStatus", headerName: "E2N" },
      { field: "E2P", headerName: "E2P" },
      { field: "OperationLeadName", headerName: "Operation Lead" },
      { field: "ProductionDate", headerName: "Production Date" },
      { field: "Project", headerName: "Project" },
      { field: "Site", headerName: "Site" },
      ...columnDefs.map((c) => ({ field: c.name, headerName: c.name })),
    ];
    exportAsCsv(rows, exportCols, "LPClassRoster");
  }, [rows, columnDefs]);

  // ─────────────────────────────────────────────────────────────────────────
  // onCellEditComplete
  //
  // Identical contract to ClassRosterGridV2 with two additions:
  //   1. Re-check milestone applicability before saving dynamic columns.
  //   2. classRosterId passed to the Merge API (may be a comma string).
  // ─────────────────────────────────────────────────────────────────────────

  const onCellEditComplete = useCallback(
    async (e) => {
      const { rowData, newRowData, field } = e;

      if (READ_ONLY_FIELDS.has(field)) return;

      const originalValue = rowData[field];
      const newValue =
        newRowData?.[field] !== undefined ? newRowData[field] : e.newValue;

      // ── Applicability guard for dynamic milestone columns ─────────────────
      // Static columns are always applicable; dynamic ones are checked here.
      // (READ_ONLY_FIELDS already excludes non-editable statics above.)
      const isDynamicCol = columnDefs.some((c) => c.name === field);
      if (
        isDynamicCol &&
        !isMilestoneApplicable(rowData.ClassRosterId, field)
      ) {
        return; // keyboard-committed edit on a grey cell — silently ignore
      }

      // ── No-op guard ───────────────────────────────────────────────────────
      const originalComparable =
        field === "TraineeStatus"
          ? rowData.TraineeStatusId
          : typeof originalValue === "number" &&
              (originalValue === 0 || originalValue === 1)
            ? Boolean(originalValue)
            : originalValue;

      if (newValue === originalComparable) return;

      // ── OpsLead — serialize OracleId from ref ─────────────────────────────
      let serialized;
      let optimisticPatch = { [field]: newValue };

      if (field === "OperationLeadName") {
        const pending = pendingOpsLeadRef.current;
        if (!pending || pending.rowId !== rowData.id) {
          showSnack("No valid ops lead selected.", "info");
          return;
        }
        serialized = serializeValue(pending.person.OracleId);
        pendingOpsLeadRef.current = null;
      } else if (field === "TraineeStatus") {
        // Editor emits numeric TraineeStatusId; API expects it stringified.
        serialized = serializeValue(newValue);
        const matchedOpt = traineeOptions.find((o) => o.value === newValue);
        optimisticPatch = {
          TraineeStatusId: newValue,
          TraineeStatus: matchedOpt?.label ?? rowData.TraineeStatus,
        };
      } else {
        serialized = serializeValue(newValue);
      }

      // ── Optimistic update ─────────────────────────────────────────────────
      setRows((prev) =>
        prev.map((r) =>
          r.id === rowData.id ? { ...r, ...optimisticPatch } : r,
        ),
      );

      // ── Persist ───────────────────────────────────────────────────────────
      try {
        setLoading(true);
        const res = await updateMergeClassTraineeMilestoneGridDetails(
          classRosterId,
          rowData.id,
          field,
          serialized,
        );

        // "NotFound" is a known backend quirk for TraineeStatus/OpsLead — treat as success
        if (res?.message === "Success" || res?.message === "NotFound") {
          showSnack(`${field} updated.`, "success");
        } else {
          const revertPatch = Object.fromEntries(
            Object.keys(optimisticPatch).map((k) => [k, rowData[k]]),
          );
          setRows((prev) =>
            prev.map((r) =>
              r.id === rowData.id ? { ...r, ...revertPatch } : r,
            ),
          );
          showSnack(`Could not save "${field}".`, "error");
        }
      } catch {
        const revertPatch = Object.fromEntries(
          Object.keys(optimisticPatch).map((k) => [k, rowData[k]]),
        );
        setRows((prev) =>
          prev.map((r) => (r.id === rowData.id ? { ...r, ...revertPatch } : r)),
        );
        showSnack("An internal error occurred.", "error");
      } finally {
        setLoading(false);
      }
    },
    [
      classRosterId,
      columnDefs,
      isMilestoneApplicable,
      showSnack,
      traineeOptions,
    ],
  );

  // ── withCellEdit — stamps onCellEditComplete + cellClassName onto editable columns ──
  const withCellEdit = useCallback(
    (colDef) => {
      if (!colDef.editor) return colDef;
      return {
        ...colDef,
        onCellEditComplete,
        cellClassName: "rg-editable-cell",
      };
    },
    [onCellEditComplete],
  );

  // ─────────────────────────────────────────────────────────────────────────
  // STATIC COLUMN DEFINITIONS
  //
  // Additions vs ClassRosterGridV2:
  //   • ClassRosterId — frozen left (context anchor for multi-roster rows)
  //   • ClassTraineeId — hidden by default (kept for data completeness)
  // ─────────────────────────────────────────────────────────────────────────

  const staticColumnDefs = useMemo(
    () => [
      // ── FROZEN LEFT ──────────────────────────────────────────────────────
      {
        field: "ClassRosterId",
        header: "Roster ID",
        frozen: true,
        alignFrozen: "left",
        sortable: true,
        style: { minWidth: "110px" },
        // No editor — read-only context column
      },
      {
        field: "TraineeOracleId",
        header: "Oracle ID",
        frozen: true,
        alignFrozen: "left",
        sortable: true,
        style: { minWidth: "130px" },
        body: (row) => (
          <OracleIdTemplate rowData={row} onInfoClick={handleInfoClick} />
        ),
      },
      {
        field: "TraineeName",
        header: "Trainee Name",
        frozen: true,
        alignFrozen: "left",
        sortable: true,
        style: { minWidth: "200px" },
        body: (row) => (
          <TraineeNameTemplate rowData={row} onInfoClick={handleInfoClick} />
        ),
      },

      // ── HIDDEN (data present, not visible by default) ────────────────────
      // PrimeReact Column doesn't have a native `hidden` prop on DataTable
      // column defs — omit from the columns array and expose via export only.
      // ClassTraineeId is already derivable via `id`; exclude from render.

      // ── SCROLLABLE COLUMNS ───────────────────────────────────────────────
      {
        field: "TraineeComment",
        header: "Comment",
        sortable: false,
        style: { minWidth: "200px" },
        body: (row) => (
          <span className={styles.commentCell}>
            {row.TraineeComment || "—"}
          </span>
        ),
      },
      {
        field: "EmployeeStatus",
        header: "Employee Status",
        sortable: true,
        style: { minWidth: "150px" },
      },
      {
        field: "TraineeStatus",
        header: "Trainee Status",
        sortable: true,
        filter: true,
        filterMatchMode: "equals",
        filterElement: (opts) => (
          <Dropdown
            value={opts.value}
            options={traineeOptions.map((o) => o.label)}
            onChange={(e) => opts.filterApplyCallback(e.value)}
            placeholder="Filter status"
            showClear
            className={styles.filterDropdown}
          />
        ),
        style: { minWidth: "160px" },
        body: (row) => (
          <TraineeStatusBodyTemplate
            rowData={row}
            traineeOptions={traineeOptions}
          />
        ),
        editor: (opts) => (
          <TraineeStatusEditor
            value={opts.rowData.TraineeStatusId}
            options={traineeOptions}
            editorCallback={opts.editorCallback}
          />
        ),
      },
      {
        field: "FTRR",
        header: "FTRR",
        sortable: true,
        style: { minWidth: "90px" },
        body: (row) => <FtrrReadonlyCell value={row.FTRR} />,
      },
      {
        field: "EndorsedToNestingStatus",
        header: "E2N",
        sortable: true,
        style: { minWidth: "90px" },
        body: (row) => (
          <BooleanIconTemplate value={row.EndorsedToNestingStatus} />
        ),
        editor: (opts) => (
          <Checkbox
            checked={!!opts.rowData.EndorsedToNestingStatus}
            onChange={(e) => opts.editorCallback(e.checked)}
          />
        ),
      },
      {
        field: "E2P",
        header: "E2P",
        sortable: true,
        style: { minWidth: "90px" },
        body: (row) => <BooleanIconTemplate value={row.E2P} />,
        editor: (opts) => (
          <Checkbox
            checked={!!opts.rowData.E2P}
            onChange={(e) => opts.editorCallback(e.checked)}
          />
        ),
      },
      {
        field: "OperationLeadName",
        header: "Operation Lead",
        sortable: true,
        style: { minWidth: "260px" },
        body: (row) => (
          <span>
            {row.OperationLeadName || (
              <span className={styles.emptyCell}>—</span>
            )}
          </span>
        ),
        editor: (opts) => (
          <OpsLeadEditor
            rowData={opts.rowData}
            editorCallback={opts.editorCallback}
            onPersonSelected={(person) => {
              pendingOpsLeadRef.current = { rowId: opts.rowData.id, person };
            }}
          />
        ),
      },
      {
        field: "ProductionDate",
        header: "Production Date",
        sortable: true,
        style: { minWidth: "180px" },
        body: (row) => <DateBodyTemplate value={row.ProductionDate} />,
        editor: (opts) => (
          <DateEditor
            value={opts.rowData.ProductionDate}
            editorCallback={opts.editorCallback}
          />
        ),
      },
      {
        field: "Project",
        header: "Project",
        sortable: true,
        style: { minWidth: "160px" },
      },
      {
        field: "Site",
        header: "Site",
        sortable: true,
        style: { minWidth: "130px" },
      },
    ],
    [traineeOptions, handleInfoClick],
  );

  // ─────────────────────────────────────────────────────────────────────────
  // DYNAMIC COLUMN DEFINITIONS
  //
  // Each body template and editor checks isMilestoneApplicable(row.ClassRosterId, col.name).
  // Non-applicable → renders <NotApplicableCell />, editor returns null.
  // ─────────────────────────────────────────────────────────────────────────

  const dynamicColumnDefs = useMemo(
    () =>
      columnDefs.map((col) => {
        const base = {
          field: col.name,
          header: col.name,
          sortable: true,
          style: {
            minWidth:
              col.type === "memo" || col.type === "multiselect"
                ? "280px"
                : "160px",
          },
        };

        switch (col.type) {
          case "multiselect":
            return {
              ...base,
              body: (row) =>
                isMilestoneApplicable(row.ClassRosterId, col.name) ? (
                  <MultiSelectBodyTemplate value={row[col.name]} />
                ) : (
                  <NotApplicableCell />
                ),
              editor: (opts) => (
                <MultiSelectEditor
                  value={opts.rowData[col.name]}
                  options={col.options}
                  editorCallback={opts.editorCallback}
                  applicable={isMilestoneApplicable(
                    opts.rowData.ClassRosterId,
                    col.name,
                  )}
                />
              ),
            };

          case "memo":
            return {
              ...base,
              body: (row) => {
                const applicable = isMilestoneApplicable(row.ClassRosterId, col.name);
                if (!applicable) return <NotApplicableCell />;
                const text = row[col.name];
                return text ? (
                  <span
                    className={styles.memoDisplay}
                    title={text}
                  >
                    {text}
                  </span>
                ) : (
                  <span className={styles.emptyCell}>—</span>
                );
              },
              editor: (opts) => (
                <MemoEditor
                  value={opts.rowData[col.name]}
                  editorCallback={opts.editorCallback}
                  applicable={isMilestoneApplicable(
                    opts.rowData.ClassRosterId,
                    col.name,
                  )}
                />
              ),
            };

          case "date":
            return {
              ...base,
              body: (row) =>
                isMilestoneApplicable(row.ClassRosterId, col.name) ? (
                  <DateBodyTemplate value={row[col.name]} />
                ) : (
                  <NotApplicableCell />
                ),
              editor: (opts) => (
                <DateEditor
                  value={opts.rowData[col.name]}
                  editorCallback={opts.editorCallback}
                  applicable={isMilestoneApplicable(
                    opts.rowData.ClassRosterId,
                    col.name,
                  )}
                />
              ),
            };

          case "singleselect":
            return {
              ...base,
              body: (row) =>
                isMilestoneApplicable(row.ClassRosterId, col.name) ? (
                  <>
                    {row[col.name] ?? (
                      <span className={styles.emptyCell}>—</span>
                    )}
                  </>
                ) : (
                  <NotApplicableCell />
                ),
              editor: (opts) => (
                <SingleSelectEditor
                  value={opts.rowData[col.name]}
                  options={col.options}
                  editorCallback={opts.editorCallback}
                  applicable={isMilestoneApplicable(
                    opts.rowData.ClassRosterId,
                    col.name,
                  )}
                />
              ),
            };

          case "number":
            return {
              ...base,
              body: (row) =>
                isMilestoneApplicable(row.ClassRosterId, col.name) ? (
                  row[col.name] != null ? (
                    <span>{row[col.name]}</span>
                  ) : (
                    <span className={styles.emptyCell}>—</span>
                  )
                ) : (
                  <NotApplicableCell />
                ),
              editor: (opts) =>
                isMilestoneApplicable(opts.rowData.ClassRosterId, col.name) ? (
                  <InputNumber
                    value={opts.rowData[col.name] ?? null}
                    onValueChange={(e) => opts.editorCallback(e.value)}
                    useGrouping={false}
                    className={styles.textInput}
                    inputClassName={styles.textInput}
                    allowEmpty
                  />
                ) : null,
            };

          default:
            return {
              ...base,
              body: (row) =>
                isMilestoneApplicable(row.ClassRosterId, col.name) ? (
                  <span>
                    {row[col.name] ?? (
                      <span className={styles.emptyCell}>—</span>
                    )}
                  </span>
                ) : (
                  <NotApplicableCell />
                ),
              editor: (opts) =>
                isMilestoneApplicable(opts.rowData.ClassRosterId, col.name) ? (
                  <InputText
                    value={opts.rowData[col.name] ?? ""}
                    onChange={(e) => opts.editorCallback(e.target.value)}
                    className={styles.textInput}
                  />
                ) : null,
            };
        }
      }),
    [columnDefs, isMilestoneApplicable],
  );

  const allColumns = useMemo(
    () => [
      {
        selectionMode: "multiple",
        frozen: true,
        alignFrozen: "left",
        headerStyle: { width: "3rem" },
        style: { width: "3rem" },
        bodyStyle: { textAlign: "center" },
      },
      ...staticColumnDefs.map(withCellEdit),
      ...dynamicColumnDefs.map(withCellEdit),
    ],
    [staticColumnDefs, dynamicColumnDefs, withCellEdit],
  );

  // ─────────────────────────────────────────────────────────────────────────
  // BULK EDIT DIALOG
  // ─────────────────────────────────────────────────────────────────────────

  const bulkDialogFooter = (
    <div className={styles.bulkDialogFooter}>
      {activeFields.length > 0 && (
        <div className={styles.bulkSummary}>
          Will update{" "}
          <strong>{activeFields.map((f) => f.label).join(", ")}</strong>
          {" "}for <strong>{selectedRows.length} row(s)</strong>.
        </div>
      )}
      <div className={styles.bulkDialogActions}>
        <Button
          variant="outlined"
          size="small"
          onClick={closeBulkDialog}
          disabled={bulkSaving}
          className={styles.bulkCancelBtn}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={applyBulkEdit}
          disabled={bulkSaving || activeFields.length === 0}
          className={styles.bulkApplyBtn}
        >
          {bulkSaving ? "Applying…" : `Apply to ${selectedRows.length} row(s)`}
        </Button>
      </div>
    </div>
  );

  // RDBulkFieldRow is defined at module level — see comment block above.

  // ─────────────────────────────────────────────────────────────────────────
  // TOOLBAR
  // ─────────────────────────────────────────────────────────────────────────

  const toolbar = (
    <div className={styles.toolbar}>
      <div className={styles.toolbarLeft}>
        <Button
          variant="contained"
          size="small"
          startIcon={<DownloadOutlinedIcon />}
          className={styles.exportBtn}
          onClick={handleExport}
          disabled={!dataReady}
        >
          Export
        </Button>
        <Button
          variant="contained"
          size="small"
          startIcon={<EditOutlinedIcon />}
          className={styles.bulkEditBtn}
          onClick={() => setBulkDialogVisible(true)}
          disabled={!dataReady || selectedRows.length === 0}
        >
          Bulk Edit{selectedRows.length > 0 ? ` (${selectedRows.length})` : ""}
        </Button>
        <span className={styles.metaItem}>
          <span className={styles.metaLabel}>Roster:</span>
          <span className={styles.metaValue}>
            {formatMultipleValues(multipleRosterDetails, "classRosterId")}
          </span>
        </span>
        <span className={styles.metaItem}>
          <span className={styles.metaLabel}>Class:</span>
          <span className={styles.metaValue}>
            {formatMultipleValues(multipleRosterDetails, "className")}
          </span>
        </span>
        <span className={styles.metaItem}>
          <span className={styles.metaLabel}>Project:</span>
          <span className={styles.metaValue}>
            {formatMultipleValues(multipleRosterDetails, "project")}
          </span>
        </span>
      </div>

      <div className={styles.toolbarRight}>
        <span className={styles.metaLabel}>Curriculum Status</span>
        <Dropdown
          value={curriculumValue}
          options={curriculumOptions}
          optionLabel="CurriculumStatus"
          dataKey="LP_CurriculumStatusId"
          onChange={(e) => handleCurriculumChange(e.value)}
          placeholder="Set status…"
          disabled={!dataReady}
          className={styles.curriculumDropdown}
          panelClassName="rg-dropdown-panel"
        />
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <>
      {loading ? <BackdropComponent /> : null}
      <div id="rd-roster-details" className={styles.root}>
        <Tooltip target=".rg-info-icon" position="top" />

        {toolbar}

        {/* ── Bulk Edit Dialog ───────────────────────────────────────────── */}
        <Dialog
          header={`Bulk Edit — ${selectedRows.length} row(s) selected`}
          visible={bulkDialogVisible}
          onHide={closeBulkDialog}
          footer={bulkDialogFooter}
          className={`${styles.bulkDialog} rg-bulk-dialog`}
        >
          <p className={styles.bulkDialogHint}>
            Enable the fields you want to update. Only checked fields will be
            applied to all selected rows.
          </p>

          {/* ── Static editable columns ── */}
          <p className={styles.bulkSectionLabel}>Standard fields</p>
          <div className={styles.bulkGrid}>
            {BULK_EDITABLE_FIELDS.map((f) => (
              <RDBulkFieldRow
                key={f.field}
                fieldDef={f}
                enabled={bulkFieldState[f.field]?.enabled ?? false}
                value={bulkFieldState[f.field]?.value ?? null}
                onToggle={(checked) => setBulkField(f.field, "enabled", checked)}
                onValueChange={(val) => setBulkField(f.field, "value", val)}
                onOpsLeadSelect={(oracleId) => { bulkOpsLeadOracleIdRef.current = oracleId; }}
                traineeOptions={traineeOptions}
                styles={styles}
                minDate={ONE_YEAR_AGO}
              />
            ))}
          </div>

          {/* ── Dynamic milestone columns ── */}
          {dialogMilestoneColumnDefs.length > 0 && (
            <>
              <p className={styles.bulkSectionLabel} style={{ marginTop: "14px" }}>Milestone columns</p>
              <div className={styles.bulkGridSingle}>
                {dialogMilestoneColumnDefs.map((col) => {
                  const fd = { field: col.name, label: col.name, type: col.type, options: col.options };
                  return (
                    <RDBulkFieldRow
                      key={col.name}
                      fieldDef={fd}
                      enabled={bulkFieldState[col.name]?.enabled ?? false}
                      value={bulkFieldState[col.name]?.value ?? null}
                      onToggle={(checked) => setBulkField(col.name, "enabled", checked)}
                      onValueChange={(val) => setBulkField(col.name, "value", val)}
                      traineeOptions={traineeOptions}
                      styles={styles}
                      minDate={ONE_YEAR_AGO}
                    />
                  );
                })}
              </div>
            </>
          )}
        </Dialog>

        <PrimeReactDataGrid
          value={rows}
          dataKey="id"
          columns={allColumns}
          enableGlobalFilter
          enableScrollable
          scrollHeight="calc(100vh - 260px)"
          editMode="cell"
          selection={selectedRows}
          onSelectionChange={(e) => setSelectedRows(e.value)}
          filterprops={INITIAL_FILTER_PROPS}
          filterDisplay="menu"
          sortField="TraineeOracleId"
          sortOrder={1}
          showGridlines
          stripedRows
          resizableColumns
          columnResizeMode="fit"
          tableStyle={{ minWidth: "1600px" }}
          emptyMessage={
            <div className={styles.emptyState}>
              <InboxOutlinedIcon className={styles.emptyIcon} />
              <p>No records to display.</p>
            </div>
          }
        />

        <OverlayPanel
          ref={overlayRef}
          showCloseIcon
          dismissable
          className={styles.overlay}
        >
          <div
            className={styles.overlayContent}
            dangerouslySetInnerHTML={{ __html: popoverHtml }}
          />
        </OverlayPanel>

        <SnackBar snack={snack} snackReset={snackReset} />
      </div>
    </>
  );
};

RosterDetailsV2.propTypes = {
  classRosterId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default RosterDetailsV2;
